import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";
import financeService, {
  BnsfFollowUpsRunOptions,
  BnsfFollowUpsSheetSummary,
  BnsfFollowUpsStartResult,
  BnsfFollowUpsVoucherResult,
} from "services/financeService";

// Same four types the upload flow's sheets carry; blank routes by whichever table resolves.
const VOUCHER_TYPES = ["", "Road", "Yard", "Radius", "Longhaul"] as const;

const statusChipColor = (status?: string): "success" | "warning" | "default" => {
  if (status === "SEND") return "success";
  if (status === "REVIEW") return "warning";
  return "default"; // "NOT DUE", or a status the page doesn't know about yet
};

type Phase = "idle" | "uploading" | "running" | "complete" | "error";
type LookupPhase = "idle" | "checking" | "checking-van";

interface RunError {
  step: string;
  message: string;
}

// Quantity source note -- always on screen while this page is open.
const QUANTITIES_CAVEAT =
  "Fuel, idle and miles are taken from BNSF trip details. Dashboard Miles is shown alongside as a " +
  "cross-check; where the two disagree, confirm the quantities before sending.";

// How long after clicking Look Up the button's text swaps from "Checking… (about 10 s)" to
// "Checking van… " when the van check is on. The lookup is one blocking HTTP call -- OrderApi
// doesn't report an interim phase for it -- so this is a purely cosmetic client-side sequencing
// timed off roughly where the base resolve+quantities call would finish, not a real server signal.
const LOOKUP_VAN_CHECK_SWITCH_MS = 4_000;

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const formatMinutes = (seconds: number): string => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes} min`;
};

// The GPS van-check verdict word ("Both due" / "veh# unresolved" / "no overlap" / ...) is already
// the last " - "-separated segment of a Van-Conflict row's own RCX Response -- OrderApi's
// BnsfFollowUpsTemplates.FormatVanConflictLine builds it that way -- so the lookup card's dedicated
// "Van check" line reuses it rather than the BFF inventing a second field that says the same thing.
const vanCheckVerdict = (rcxResponse?: string): string => {
  if (!rcxResponse || !rcxResponse.includes(" - ")) return "—";
  return rcxResponse.split(" - ").pop() as string;
};

// The two run options, worded for the biller rather than the implementation. Shared by the upload
// card and the single-voucher card so the two can never drift apart -- they are the same option.
const VAN_CHECK_LABEL = "Resolve van conflicts from GPS";
const VAN_CHECK_HELP =
  "When BNSF says two vouchers used the same van, work out from GPS which record has the wrong veh# instead of sending it back for review. Road trips only.";
const REOPEN_NOTES_LABEL = "Explain reopened trips";
// Deliberately blunt about the cost. This said "about a minute per 10 rows" while the measured
// figure is over two minutes PER ROW -- someone ticking it for a 50-row sheet expected five
// minutes and would have got over an hour.
const REOPEN_NOTES_HELP =
  "Very slow — roughly 2 minutes per row, so a 50-row sheet takes over an hour. Leave this off unless you need the notes for a handful of rows.";

/** What the Look Up button is waiting for, or null when it is ready. */
const lookupBlockedReason = (voucherId: string, phase: string): string | null => {
  if (!voucherId.trim()) return "Enter a voucher ID or short key to look one up.";
  if (phase !== "idle") return "Finishing the last lookup…";
  return null;
};

/**
 * Finance > BNSF Follow-Ups.
 *
 * Uploads BNSF's recurring dispute workbook and runs it through OrderApi's classifier. There is
 * no background job on the BFF for the ~20-minute run -- the browser drives it, calling
 * next-batch in a tight loop and updating progress from each response, until isComplete, then
 * downloading the filled workbook. A batch/step failure stops the loop and is shown inline with
 * a retry (resume the loop) or cancel (abandon the run) choice; it never silently drops the rows
 * already processed.
 *
 * The two run options (van conflicts from GPS, default ON; reopened-trip notes, default OFF and
 * slow) are picked once,
 * before Start Run, and apply for the run's whole lifetime -- Aaron's house rule for this feature
 * is that the user always sees what's happening and how long is left, so the choice up front also
 * drives the up-front "N vouchers · M van-conflict rows · ~X min" estimate and the in-progress
 * "about N min left" figure below.
 */
const BnsfFollowUps = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unmountedRef = useRef(false);
  useEffect(() => () => {
    unmountedRef.current = true;
  }, []);

  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [runId, setRunId] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState("");
  const [sheets, setSheets] = useState<BnsfFollowUpsSheetSummary[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [processedRows, setProcessedRows] = useState(0);
  const [runError, setRunError] = useState<RunError | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Guards against a double-click/re-entrant Start or Retry firing two overlapping batch loops
  // for the same run -- a ref rather than state so the check is synchronous within the click
  // handler itself, not dependent on a re-render having removed/disabled the button yet
  // (review.md #1's UI half; the BFF's own per-run lock is the other half of this fix).
  const batchLoopActiveRef = useRef(false);
  const [isBatchInFlight, setIsBatchInFlight] = useState(false);

  // "GPS van check" / "Reopen change notes" -- picked once before Start Run, fixed for the run.
  const [runOptions, setRunOptions] = useState<BnsfFollowUpsRunOptions>({
    includeVanCheck: true,
    includeReopenNotes: false,
  });

  // Up-front estimate ("N vouchers · M van-conflict rows · ~X min"), from /start.
  const [startEstimate, setStartEstimate] = useState<BnsfFollowUpsStartResult | null>(null);

  // Progress detail beyond the row count: batch X of Y, the slowest step OrderApi reported for
  // the last batch, and the BFF's own rolling-average "about N min left".
  const [batchNumber, setBatchNumber] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [currentStep, setCurrentStep] = useState<string | undefined>(undefined);
  const [estimatedSecondsRemaining, setEstimatedSecondsRemaining] = useState<number | undefined>(undefined);

  // Elapsed time since Start Run -- ticks every second while running so it keeps moving between
  // (up to ~40s) batch calls, not just when a new one lands.
  const runStartedAtRef = useRef<number | null>(null);
  // The tick value itself is never read -- setting it is only how this component forces a
  // re-render every second so the elapsed-time line below keeps moving between batch responses
  // (a single batch call can take up to ~40s).
  const [, forceElapsedTick] = useState(0);
  useEffect(() => {
    if (phase !== "running") return undefined;
    const interval = setInterval(() => forceElapsedTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Single-voucher lookup (Brendon's ask) -- entirely separate from the upload/run state above;
  // it hits the same OrderApi batch call with a one-element list and shows the one result back.
  const [lookupVoucherId, setLookupVoucherId] = useState("");
  const [lookupVoucherType, setLookupVoucherType] = useState("");
  const [lookupComment, setLookupComment] = useState("");
  const [lookupOptions, setLookupOptions] = useState<BnsfFollowUpsRunOptions>({
    includeVanCheck: true,
    includeReopenNotes: false,
  });
  const [lookupPhase, setLookupPhase] = useState<LookupPhase>("idle");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<BnsfFollowUpsVoucherResult | null>(null);
  const lookupVanCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (lookupVanCheckTimeoutRef.current) clearTimeout(lookupVanCheckTimeoutRef.current);
    },
    []
  );

  const handleFileSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setFile(files[0]);
    setRunError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drives next-batch until the run completes, fails, or the runId is no longer known. Each
  // response's processedRows/totalRows (plus the batch/step/estimate detail added 2026-09-20) IS
  // the progress update -- there is nothing else to poll. financeService.nextBnsfFollowUpsBatch is
  // documented to never throw -- every failure it can identify comes back as an "expired"/"failed"
  // outcome -- but the try/catch below is kept as a last line of defense: this loop must never let
  // an exception escape unhandled (test.md Defect B's CRA "Uncaught runtime errors" overlay),
  // whatever the cause.
  const driveBatchLoop = useCallback(async (activeRunId: string) => {
    if (batchLoopActiveRef.current) return; // a loop for this run is already in flight -- ignore
    batchLoopActiveRef.current = true;
    setIsBatchInFlight(true);
    try {
      for (;;) {
        let outcome;
        try {
          outcome = await financeService.nextBnsfFollowUpsBatch(activeRunId);
        } catch (e: any) {
          if (unmountedRef.current) return;
          setRunError({ step: "batch", message: e?.message ?? "The batch failed and the run cannot continue." });
          setPhase("error");
          toast.error("BNSF Follow-Ups: batch failed.");
          return;
        }
        if (unmountedRef.current) return;

        if (outcome.status === "expired") {
          setRunError({ step: "batch", message: "The run expired on the server (or the BFF restarted). Start again." });
          setPhase("error");
          toast.error("BNSF Follow-Ups run expired.");
          return;
        }
        if (outcome.status === "failed") {
          setRunError({ step: outcome.failedStep, message: outcome.message });
          setPhase("error");
          toast.error(`BNSF Follow-Ups: ${outcome.failedStep} failed.`);
          return;
        }

        setProcessedRows(outcome.processedRows);
        setTotalRows(outcome.totalRows);
        setBatchNumber(outcome.batchNumber);
        setBatchCount(outcome.batchCount);
        setCurrentStep(outcome.currentStep);
        setEstimatedSecondsRemaining(outcome.estimatedSecondsRemaining);

        if (outcome.isComplete) {
          setPhase("complete");
          return;
        }
      }
    } finally {
      batchLoopActiveRef.current = false;
      setIsBatchInFlight(false);
    }
  }, []);

  const handleStart = async () => {
    if (!file) return;
    setPhase("uploading");
    setRunError(null);
    setDownloadError(null);
    try {
      const started = await financeService.startBnsfFollowUps(file, runOptions);
      setRunId(started.runId);
      setDownloadName(`${file.name.replace(/\.xlsx$/i, "")} (RCX).xlsx`);
      setSheets(started.sheets);
      setTotalRows(started.totalRows);
      setProcessedRows(0);
      setStartEstimate(started);
      setBatchNumber(0);
      setBatchCount(started.batchCount);
      setCurrentStep(undefined);
      setEstimatedSecondsRemaining(started.estimatedSeconds);
      runStartedAtRef.current = Date.now();
      forceElapsedTick(0);
      setPhase("running");
      driveBatchLoop(started.runId);
    } catch (e: any) {
      setRunError({ step: "upload", message: e?.message ?? "Upload failed. Check the file and try again." });
      setPhase("error");
      toast.error("BNSF Follow-Ups upload failed.");
    }
  };

  const handleRetryBatch = () => {
    if (!runId) return;
    setRunError(null);
    setPhase("running");
    driveBatchLoop(runId);
  };

  const handleCancel = () => {
    setPhase("idle");
    setRunId(null);
    setSheets([]);
    setTotalRows(0);
    setProcessedRows(0);
    setRunError(null);
    setDownloadError(null);
    setFile(null);
    setStartEstimate(null);
    setBatchNumber(0);
    setBatchCount(0);
    setCurrentStep(undefined);
    setEstimatedSecondsRemaining(undefined);
    runStartedAtRef.current = null;
  };

  const handleDownload = async () => {
    if (!runId) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const blob = await financeService.downloadBnsfFollowUps(runId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName || "BNSF Follow-Ups (RCX).xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      handleCancel();
    } catch (e: any) {
      toast.error("BNSF Follow-Ups download failed.");
      setDownloadError(e?.message ?? "Download failed. Try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleLookup = async () => {
    if (!lookupVoucherId.trim()) return;
    setLookupPhase("checking");
    setLookupError(null);
    setLookupResult(null);

    // The lookup is one blocking call -- OrderApi doesn't report an interim "now checking van"
    // phase for it the way a batch's Steps list does. This is a purely cosmetic client-side text
    // swap timed off the BFF's own base estimate, not a real server-reported phase change.
    if (lookupOptions.includeVanCheck) {
      lookupVanCheckTimeoutRef.current = setTimeout(() => {
        if (!unmountedRef.current) setLookupPhase("checking-van");
      }, LOOKUP_VAN_CHECK_SWITCH_MS);
    }

    try {
      const result = await financeService.lookupBnsfFollowUp({
        voucherId: lookupVoucherId.trim(),
        voucherType: lookupVoucherType,
        comment: lookupComment.trim(),
        includeVanCheck: lookupOptions.includeVanCheck,
        includeReopenNotes: lookupOptions.includeReopenNotes,
      });
      setLookupResult(result);
    } catch (e: any) {
      const message = e?.message ?? "Lookup failed. Check the voucher ID and try again.";
      setLookupError(message);
      toast.error("BNSF Follow-Ups: lookup failed.");
    } finally {
      if (lookupVanCheckTimeoutRef.current) {
        clearTimeout(lookupVanCheckTimeoutRef.current);
        lookupVanCheckTimeoutRef.current = null;
      }
      setLookupPhase("idle");
    }
  };

  const handleCopyRcxResponse = async () => {
    if (!lookupResult?.rcxResponse) return;
    try {
      await navigator.clipboard.writeText(lookupResult.rcxResponse);
      toast.success("RCX Response copied.");
    } catch (e) {
      toast.error("Could not copy to clipboard.");
    }
  };

  const progressPercent = totalRows > 0 ? Math.min(100, Math.round((processedRows / totalRows) * 100)) : 0;
  const canStart = !!file && !runId && (phase === "idle" || phase === "error") && !isBatchInFlight;
  const canLookup = !!lookupVoucherId.trim() && lookupPhase === "idle";
  const lookupBlocked = lookupBlockedReason(lookupVoucherId, lookupPhase);
  const optionsLocked = phase !== "idle" && phase !== "error";
  const elapsedMs = runStartedAtRef.current ? Date.now() - runStartedAtRef.current : 0;

  const lookupButtonLabel =
    lookupPhase === "checking"
      ? "Checking… (about 10 s)"
      : lookupPhase === "checking-van"
      ? "Checking van… "
      : "Look Up";

  return (
    <Stack spacing={3}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          BNSF Follow-Ups
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload BNSF&apos;s recurring Follow-Ups workbook (.xlsx). Every disputed voucher is
          resolved to its trip or shift, classified, and written back to the sheet it came from.
          Download the filled workbook once the run completes.
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          {QUANTITIES_CAVEAT}
        </Alert>

        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={(e) => handleFileSelected(e.target.files)}
          />
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              disabled={phase === "uploading" || phase === "running" || phase === "complete"}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </MDButton>
            <Typography variant="body2" color={file ? "text.primary" : "text.secondary"}>
              {file ? file.name : "No file selected"}
            </Typography>
          </MDBox>

          {/* Both options are named for what they get the biller, not for the mechanism behind
              them: "GPS van check" and "Reopen change notes" told a reader nothing about whether
              to tick them, and the only caption said what one COSTS without saying what it buys. */}
          <MDBox>
            <MDBox>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runOptions.includeVanCheck}
                    disabled={optionsLocked}
                    onChange={(e) => setRunOptions((o) => ({ ...o, includeVanCheck: e.target.checked }))}
                  />
                }
                label={VAN_CHECK_LABEL}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                {VAN_CHECK_HELP}
              </Typography>
            </MDBox>
            <MDBox sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runOptions.includeReopenNotes}
                    disabled={optionsLocked}
                    onChange={(e) => setRunOptions((o) => ({ ...o, includeReopenNotes: e.target.checked }))}
                  />
                }
                label={REOPEN_NOTES_LABEL}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                {REOPEN_NOTES_HELP}
              </Typography>
            </MDBox>
          </MDBox>

          <MDButton variant="contained" color="success" disabled={!canStart} onClick={handleStart}>
            {phase === "uploading" ? "Uploading…" : "Start Run"}
          </MDButton>
        </Stack>

        {sheets.length > 0 && phase !== "idle" && phase !== "uploading" && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {sheets.map((sheet) => (
              <Chip
                key={sheet.name}
                size="small"
                color={sheet.skipped ? "default" : "info"}
                label={sheet.skipped ? `${sheet.name} (skipped)` : `${sheet.name}: ${sheet.rowCount} rows`}
              />
            ))}
          </Stack>
        )}

        {startEstimate && (phase === "running" || phase === "complete") && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {startEstimate.voucherCount} voucher{startEstimate.voucherCount === 1 ? "" : "s"} ·{" "}
            {startEstimate.vanConflictRowCount} van-conflict row{startEstimate.vanConflictRowCount === 1 ? "" : "s"} · ~
            {formatMinutes(startEstimate.estimatedSeconds)}
          </Typography>
        )}

        {phase === "running" && (
          <MDBox mt={2}>
            <MDProgress color="info" value={progressPercent} label />
            <Typography variant="caption" color="text.secondary" display="block">
              Rows {processedRows} / {totalRows}
              {batchCount > 0 ? ` · batch ${Math.min(batchNumber, batchCount)} of ${batchCount}` : ""}
              {` · elapsed ${formatElapsed(elapsedMs)}`}
              {estimatedSecondsRemaining != null ? ` · about ${formatMinutes(estimatedSecondsRemaining)} left` : ""}
              {currentStep ? ` · step: ${currentStep}` : ""}
            </Typography>
          </MDBox>
        )}

        {phase === "complete" && (
          <MDBox mt={2}>
            <MDProgress color="success" value={progressPercent} label />
            <Typography variant="caption" color="text.secondary">
              {processedRows} / {totalRows} rows processed
            </Typography>
          </MDBox>
        )}

        {phase === "error" && runError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              {runError.step === "upload" ? "Upload failed" : `Failed: ${runError.step}`}
            </Typography>
            <Typography variant="body2">{runError.message}</Typography>
            {runId && (
              <Stack direction="row" spacing={1} mt={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  disabled={isBatchInFlight}
                  onClick={handleRetryBatch}
                >
                  Retry
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={isBatchInFlight}
                  onClick={handleCancel}
                >
                  Cancel
                </MDButton>
              </Stack>
            )}
          </Alert>
        )}

        {phase === "complete" && (
          <MDBox mt={2}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Run complete — {totalRows} row{totalRows === 1 ? "" : "s"} processed.
            </Alert>
            {downloadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {downloadError}
              </Alert>
            )}
            <MDButton variant="contained" color="success" disabled={isDownloading} onClick={handleDownload}>
              {isDownloading ? "Downloading…" : "Download Filled Workbook"}
            </MDButton>
          </MDBox>
        )}
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Check one voucher
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Look up a single disputed voucher without uploading a workbook. Runs through the same
          classifier as the upload above.
        </Typography>

        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <TextField
            required
            label="Voucher ID"
            placeholder="GUID or short key — short keys are not unique"
            helperText="Required. The comment alone is not enough to look a voucher up."
            value={lookupVoucherId}
            onChange={(e) => setLookupVoucherId(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Voucher type"
            value={lookupVoucherType}
            onChange={(e) => setLookupVoucherType(e.target.value)}
            size="small"
            fullWidth
          >
            {VOUCHER_TYPES.map((type) => (
              <MenuItem key={type || "blank"} value={type}>
                {type || "(blank)"}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="BNSF Comment"
            placeholder="Paste the sheet's comment — rules key off the peer GUID, &quot;tied up&quot;, &quot;line items&quot;, a drop off event, etc."
            value={lookupComment}
            onChange={(e) => setLookupComment(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            fullWidth
          />

          <MDBox>
            <MDBox>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lookupOptions.includeVanCheck}
                    disabled={lookupPhase !== "idle"}
                    onChange={(e) => setLookupOptions((o) => ({ ...o, includeVanCheck: e.target.checked }))}
                  />
                }
                label={VAN_CHECK_LABEL}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                {VAN_CHECK_HELP}
              </Typography>
            </MDBox>
            <MDBox sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lookupOptions.includeReopenNotes}
                    disabled={lookupPhase !== "idle"}
                    onChange={(e) => setLookupOptions((o) => ({ ...o, includeReopenNotes: e.target.checked }))}
                  />
                }
                label={REOPEN_NOTES_LABEL}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                {REOPEN_NOTES_HELP}
              </Typography>
            </MDBox>
          </MDBox>

          {/* A disabled button that says nothing is the same bug as a blank screen: the reader is
              left guessing. Voucher ID is the only thing that gates this, so name it. */}
          <MDBox>
            <MDButton variant="contained" color="success" disabled={!canLookup} onClick={handleLookup}>
              {lookupButtonLabel}
            </MDButton>
            {lookupBlocked ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {lookupBlocked}
              </Typography>
            ) : null}
          </MDBox>
        </Stack>

        {lookupError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {lookupError}
          </Alert>
        )}

        {lookupResult && (
          <MDBox mt={3}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" alignItems="flex-start" spacing={1} mb={1}>
              <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: "pre-wrap" }}>
                <strong>RCX Response:</strong> {lookupResult.rcxResponse || "—"}
              </Typography>
              {lookupResult.rcxResponse && (
                <Tooltip title="Copy RCX Response">
                  <IconButton size="small" onClick={handleCopyRcxResponse}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={statusChipColor(lookupResult.rcxStatus)}
                label={lookupResult.rcxStatus || "UNKNOWN"}
              />
              {lookupResult.rcxProblemType && <Chip size="small" label={lookupResult.rcxProblemType} />}
            </Stack>

            <Typography variant="body2">
              <strong>Fuel / Idle / Miles / Shift:</strong>{" "}
              {lookupResult.fuel ?? "—"} / {lookupResult.idle ?? "—"} / {lookupResult.miles ?? "—"} /{" "}
              {lookupResult.shift ?? "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Dashboard Miles:</strong> {lookupResult.dashboardMiles ?? "—"}
            </Typography>
            <Typography variant="body2">
              <strong>Fit:</strong> {lookupResult.fitsTripTime || "—"}
            </Typography>
            <Typography variant="body2">
              <strong>OTP / Driver Early:</strong> {lookupResult.otpStatus || "—"}
              {lookupResult.driverEarlyMin != null ? ` / ${lookupResult.driverEarlyMin} min` : ""}
            </Typography>
            {lookupResult.rcxProblemType === "Van-Conflict" && (
              <Typography variant="body2">
                <strong>Van check:</strong> {vanCheckVerdict(lookupResult.rcxResponse)}
                {lookupResult.correctVehicle != null ? ` — correct veh# ${lookupResult.correctVehicle}` : ""}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              <strong>Evidence/Note:</strong> {lookupResult.rcxEvidenceNote || "—"}
            </Typography>

            <Alert severity="warning" sx={{ mt: 2 }}>
              {QUANTITIES_CAVEAT}
            </Alert>
          </MDBox>
        )}
      </Card>
    </Stack>
  );
};

export default BnsfFollowUps;
